import L, { type LatLng, type LatLngBounds } from "leaflet";
import * as _ from "lodash";
import { v4 as uuidv4 } from "uuid";

import { Map } from "@/utils";

import collections from "../../utils/map/collections";
import { isNumericFlag, type NumericFlag } from "../flag";

export interface LightFixtureType {
    id: number;
    name: string;
    width: number;
    length: number;
    isCLS: boolean;
    interiorWidth?: number;
    interiorLength?: number;
}

interface LightOptions {
    uniqueId?: string;
    id?: number;
    ciCode?: number;
    locationDescription?: string;
    VLCEnabled?: boolean | null;
    bleId?: string;
    BLEBeaconUUId?: string | null;
    flagNotes?: string;
    luminairePositionId?: number;
    flags?: NumericFlag[];
    z?: number;
    tag?: string | number;
    x1?: number;
    x3?: number;
    y1?: number;
    y3?: number;
    controlGroupId?: string;
    floorSpaceCatalog_ID?: number;
    positioningType?: string;
    [key: string]: unknown;
}

interface LightPoint extends LatLng {
    lngOriginal?: string;
    latOriginal?: string;
    positionZOriginal?: string;
}

interface Dimension {
    width: number | (number | string)[];
    length: number | (number | string)[];
    shouldAppendDot?: boolean;
    [key: string]: unknown;
}

interface Position {
    x: number | string;
    y: number | string;
    z: number | string;
}

interface CeilingDetails {
    tileSizeX: number;
    tileSizeY: number;
    gridOffSetX: number;
    gridOffSetY: number;
}

/**
 * Represents a light fixture on the map with its position, dimensions, and metadata.
 */
class Light {
    _id: string;
    point: LightPoint;
    lastPoint: LightPoint;
    type: LightFixtureType;
    _externalDimension: Dimension;
    _signalAperture: Dimension;
    id: number;
    floorSpaceId: number;
    ciCode: number;
    locationDescription: string;
    vlcIsActive: boolean | null;
    bleId: string;
    bleUUID: string | null;
    notes: string;
    positionId: number;
    selectedFlags: NumericFlag[];
    selected: boolean;
    positionZ: number;
    _persistedTag: number;
    options: LightOptions | undefined;
    group: string | undefined;
    lastDimensions: LatLngBounds | undefined;
    positionZOriginal: string | undefined;
    positioningType: string;
    defectLight?: boolean;

    /**
     * Creates a new Light instance from a map point, fixture type, floor space ID, and optional metadata.
     *
     * @param point - The Leaflet LatLng position of the light.
     * @param type - The fixture type with dimension metadata.
     * @param floorSpaceId - The ID of the floor space this light belongs to.
     * @param options - Optional additional properties (uniqueId, bleId, flags, etc.).
     */
    constructor(
        point: LightPoint,
        type: LightFixtureType,
        floorSpaceId: number,
        options?: LightOptions,
    ) {
        this._id = options && options.uniqueId ? options.uniqueId : uuidv4();
        this.point = point;
        this.lastPoint = point;
        this.type = type;
        this._externalDimension = { width: type.width, length: type.length };
        if (type.interiorLength && type.interiorWidth) {
            this._signalAperture = {
                width: type.interiorWidth,
                length: type.interiorLength,
            };
        } else if (options) {
            this._signalAperture = {
                width: Math.abs(options.x1 - options.x3),
                length: Math.abs(options.y1 - options.y3),
            };
        } else {
            this._signalAperture = this._externalDimension;
        }
        this.id = options ? options.id : 0;
        this.floorSpaceId = floorSpaceId;
        this.ciCode = options ? options.ciCode : 0;
        this.locationDescription = options ? options.locationDescription : "";
        this.vlcIsActive =
            options && !_.isNil(options.VLCEnabled) ? options.VLCEnabled : null;
        this.bleId = options ? options.bleId : "0";
        this.bleUUID =
            !options ||
            !options.BLEBeaconUUId ||
            options.BLEBeaconUUId.toString() === "0"
                ? null
                : options.BLEBeaconUUId;
        this.notes = options ? options.flagNotes : "";
        this.positionId = options ? options.luminairePositionId : 0;
        this.selectedFlags = options && options.flags ? options.flags : [];
        this.selected = false;
        this.positionZ = options ? options.z : 0;
        this._persistedTag = options?.tag
            ? parseInt(String(options.tag), 10) || 0
            : 0;
        this.options = options;
        this.positioningType = options?.positioningType ?? "";
        this.group = undefined;
    }

    /**
     * Gets the unique identifier for this light.
     *
     * @returns The unique identifier.
     */
    get uniqueId() {
        return this._id;
    }

    /**
     * Gets the computed flags for this light.
     *
     * @returns The computed flags array.
     */
    get flags() {
        const numericFlags = collections.flags.filter(isNumericFlag);
        let selectedFlags = [...this.selectedFlags];

        _.remove(
            selectedFlags,
            (selectedFlag) =>
                selectedFlag.value === 128 || selectedFlag.value === 2048,
        );

        const autoFlagNoBle = numericFlags.find(
            (flag) =>
                this.bleId === "0" &&
                this.id &&
                this.id !== 0 &&
                flag.value === 128,
        );
        const autoFlagNoUUIDBle = numericFlags.find(
            (flag) =>
                !this.bleUUID &&
                this.id &&
                this.id !== 0 &&
                flag.value === 2048,
        );
        const alreadySet = _.some(selectedFlags, autoFlagNoBle);
        const alreadySetNoUUIDBle = _.some(selectedFlags, autoFlagNoUUIDBle);

        if (autoFlagNoBle && !alreadySet) {
            selectedFlags = [...selectedFlags, autoFlagNoBle];
        }

        if (autoFlagNoUUIDBle && !alreadySetNoUUIDBle) {
            selectedFlags = [...selectedFlags, autoFlagNoUUIDBle];
        }

        if (this.isCLS) {
            const emergencyLightFlag = numericFlags.find(
                (flag) => flag.label === "Emergency Light",
            );

            if (emergencyLightFlag) {
                selectedFlags.push(emergencyLightFlag);
            }

            if (!this.hasLocationDescription) {
                const missingLocationDescriptionFlag = numericFlags.find(
                    (flag) => flag.label === "Missing Location Description",
                );

                if (emergencyLightFlag && missingLocationDescriptionFlag) {
                    selectedFlags.push(missingLocationDescriptionFlag);
                }
            }
        }
        return selectedFlags;
    }

    /**
     * Gets the computed tag bitmask for this light's current flags.
     *
     * @returns The numeric tag value.
     */
    get computedTag() {
        return _.sumBy(this.flags, (flag) => flag.value);
    }

    /**
     * Gets the light position in feet.
     *
     * @returns The position with x, y, and z coordinates.
     */
    get position() {
        return {
            x: this.point.lngOriginal
                ? this.point.lngOriginal
                : Map.Converter.convertMeterToFeet(this.point.lng),
            y: this.point.latOriginal
                ? this.point.latOriginal
                : Map.Converter.convertMeterToFeet(this.point.lat),
            z: this.point.positionZOriginal
                ? this.point.positionZOriginal
                : Map.Converter.convertMeterToFeet(this.positionZ),
        };
    }

    /**
     * Sets the light position.
     */
    set position(value: Partial<Position>) {
        const position = _.defaultsDeep({}, value, this.position);
        delete this.point.lngOriginal;
        delete this.point.latOriginal;
        delete this.point.positionZOriginal;
        this.point.lng = Map.Converter.convertFeetToMeters(position.x);
        const checkValue = (valueCheck: string | number) =>
            valueCheck.toString() === "-" ||
            valueCheck.toString().indexOf(".") ===
                valueCheck.toString().length - 1 ||
            (valueCheck.toString().split(".")[1] &&
                valueCheck.toString().split(".")[1].length >= 3);

        if (checkValue(position.x)) {
            this.point.lngOriginal = position.x.toString();
        }
        this.point.lat = Map.Converter.convertFeetToMeters(position.y);

        if (checkValue(position.y)) {
            this.point.latOriginal = position.y.toString();
        }

        this.positionZ = Map.Converter.convertFeetToMeters(position.z);
        if (checkValue(position.z)) {
            this.positionZOriginal = position.z.toString();
        }
    }

    /**
     * Gets the signal aperture dimensions and bounds in both feet and lat/lng coordinates.
     *
     * @returns The signal aperture with bounds, dimensions, width, and length.
     */
    get signalAperture(): {
        bounds: LatLngBounds;
        dimensions: Record<string, number>;
        width: number;
        length: number;
    } {
        const { point } = this;
        const signalAperture = {
            width: this.getValueFromInternal(this._signalAperture.width, true),
            length: this.getValueFromInternal(
                this._signalAperture.length,
                true,
            ),
        };
        const dimensions = {
            x1:
                isNaN(point.lng) || isNaN(signalAperture.width)
                    ? 0
                    : point.lng -
                      Map.Converter.convertFeetToMeters(
                          signalAperture.width / 2,
                      ),
            x2:
                isNaN(point.lng) || isNaN(signalAperture.width)
                    ? 0
                    : point.lng +
                      Map.Converter.convertFeetToMeters(
                          signalAperture.width / 2,
                      ),
            y1:
                isNaN(point.lat) || isNaN(signalAperture.length)
                    ? 0
                    : point.lat -
                      Map.Converter.convertFeetToMeters(
                          signalAperture.length / 2,
                      ),
            y2:
                isNaN(point.lat) || isNaN(signalAperture.length)
                    ? 0
                    : point.lat +
                      Map.Converter.convertFeetToMeters(
                          signalAperture.length / 2,
                      ),
        };
        const d1 = L.latLng(dimensions.y1, dimensions.x1);
        const d2 = L.latLng(dimensions.y2, dimensions.x2);
        return {
            bounds: L.latLngBounds(d1, d2),
            dimensions,
            width: this.getValueFromInternal(this._signalAperture.width),
            length: this.getValueFromInternal(this._signalAperture.length),
        };
    }

    /**
     * Sets the signal aperture dimensions.
     */
    set signalAperture(value: Record<string, unknown>) {
        const signalAperture = _.defaultsDeep(
            {},
            value,
            this.signalAperture,
            this.externalDimension,
        );
        const propName = Object.keys(value)[0];
        const shouldAppendDot =
            typeof value[propName] === "string" &&
            ((value[propName] === "0" &&
                this.signalAperture[propName] !== "0.") ||
                (value[propName].indexOf(".") > 0 &&
                    value[propName].indexOf(".") ===
                        value[propName].length - 1));
        this._signalAperture = {
            width: Map.Converter.convertFeetToMeters(signalAperture.width),
            length: Map.Converter.convertFeetToMeters(signalAperture.length),
            shouldAppendDot,
        };
        if (shouldAppendDot) {
            this._signalAperture[propName] = [
                this._signalAperture[propName],
                ".",
            ];
        }
    }

    /**
     * Gets the fixture type of this light.
     *
     * @returns The fixture type.
     */
    get fixtureType(): LightFixtureType {
        return this.type;
    }

    /**
     * Sets the fixture type, resizing the external dimensions and signal aperture to
     * the new type's defaults while keeping the fixture's current orientation.
     */
    set fixtureType(newType: LightFixtureType) {
        const { width: currentWidth, length: currentLength } =
            this.externalDimension;

        // No rotation flag is persisted, so infer it by comparing the fixture's current
        // orientation against the orientation its type declares.
        const isRotated =
            this.type.width !== this.type.length &&
            currentWidth > currentLength !== this.type.width > this.type.length;

        const orient = (width: number, length: number) =>
            isRotated ? { width: length, length: width } : { width, length };

        const externalDimension = orient(newType.width, newType.length);

        this.type = newType;
        this._externalDimension = externalDimension;

        this._signalAperture =
            newType.interiorWidth && newType.interiorLength
                ? orient(newType.interiorWidth, newType.interiorLength)
                : { ...externalDimension };

        this.lastDimensions = undefined;
    }

    getValueFromInternal = (
        prop: number | (number | string)[],
        ignoreDot?: boolean,
    ): number => {
        let value: number | string = prop as number;
        let dot = "";
        if (prop instanceof Array) {
            value = prop[0] as number;
            dot = prop[1] as string;
        }
        const convertedValue = Map.Converter.convertMeterToFeet(value, 6);
        return dot && !ignoreDot
            ? Number(`${convertedValue}${dot}`)
            : convertedValue;
    };

    /**
     * Gets the external (physical) dimensions of the light fixture in feet.
     *
     * @returns The external dimensions with width and length.
     */
    get externalDimension(): { width: number; length: number } {
        return {
            width: this.getValueFromInternal(this._externalDimension.width),
            length: this.getValueFromInternal(this._externalDimension.length),
        };
    }

    /**
     * Sets the external dimensions.
     */
    set externalDimension(value: Record<string, unknown>) {
        const externalDimension = _.defaultsDeep(
            {},
            value,
            this.externalDimension,
        );
        const propName = Object.keys(value)[0];
        const shouldAppendDot =
            typeof value[propName] === "string" &&
            ((value[propName] === "0" &&
                this.signalAperture[propName] !== "0.") ||
                (value[propName].indexOf(".") > 0 &&
                    value[propName].indexOf(".") ===
                        value[propName].length - 1));
        this._externalDimension = {
            width: Map.Converter.convertFeetToMeters(externalDimension.width),
            length: Map.Converter.convertFeetToMeters(externalDimension.length),
        };
        if (shouldAppendDot) {
            this._externalDimension[propName] = [
                this._externalDimension[propName],
                ".",
            ];
        }
    }

    /**
     * Gets the fixture corner coordinates in lat/lng based on external dimensions.
     *
     * @returns The corner coordinates.
     */
    get dimensions() {
        const { point } = this;
        const width = this.externalDimension.width as number;
        const length = this.externalDimension.length as number;
        return {
            x1: point.lng - Map.Converter.convertFeetToMeters(width / 2),
            x2: point.lng - Map.Converter.convertFeetToMeters(width / 2),
            x3: point.lng + Map.Converter.convertFeetToMeters(width / 2),
            y1: point.lat - Map.Converter.convertFeetToMeters(length / 2),
            y2: point.lat + Map.Converter.convertFeetToMeters(length / 2),
            y3: point.lat + Map.Converter.convertFeetToMeters(length / 2),
        };
    }

    /**
     * Gets the negated fixture corner coordinates, used for API conversion.
     *
     * @returns The negated corner coordinates.
     */
    get invertedDimensions() {
        const { point } = this;
        const width = this.signalAperture.width as number;
        const length = this.signalAperture.length as number;
        const dimensions = {
            x1: point.lng - Map.Converter.convertFeetToMeters(width / 2),
            x2: point.lng - Map.Converter.convertFeetToMeters(width / 2),
            x3: point.lng + Map.Converter.convertFeetToMeters(width / 2),
            y1: point.lat - Map.Converter.convertFeetToMeters(length / 2),
            y2: point.lat + Map.Converter.convertFeetToMeters(length / 2),
            y3: point.lat + Map.Converter.convertFeetToMeters(length / 2),
        };
        return {
            x1: dimensions.x1 * -1,
            x2: dimensions.x2 * -1,
            x3: dimensions.x3 * -1,
            y1: dimensions.y1 * -1,
            y2: dimensions.y2 * -1,
            y3: dimensions.y3 * -1,
        };
    }

    /**
     * Gets the Leaflet bounding box for this light fixture.
     *
     * @returns The Leaflet LatLngBounds.
     */
    get bounds() {
        const { y1, x1, y3, x3 } = this.dimensions;
        if (!this.lastDimensions) {
            this.lastDimensions = L.latLngBounds(
                L.latLng(0, 0),
                L.latLng(0, 0),
            );
        }
        let d1;
        let d3;
        if (!isNaN(y1) && !isNaN(x1)) {
            d1 = L.latLng(y1, x1);
        }
        if (!isNaN(y3) && !isNaN(x3)) {
            d3 = L.latLng(y3, x3);
        }
        if (d1 && d3) {
            this.lastDimensions = L.latLngBounds(d1, d3);
            return this.lastDimensions;
        }

        return this.lastDimensions;
    }

    /**
     * Gets the four corner lat/lng points of the fixture.
     *
     * @returns The array of four corner LatLng points.
     */
    get fixtureLatLngs() {
        const l = this.dimensions;
        const d1 = L.latLng(l.y1, l.x1);
        const d2 = L.latLng(l.y2, l.x2);
        const d3 = L.latLng(l.y3, l.x3);
        const d4 = L.latLng(l.y1, l.x3);
        return [d1, d2, d3, d4];
    }

    /**
     * Whether this light is a CLS fixture.
     *
     * @returns Whether this light is CLS.
     */
    get isCLS() {
        return this.type.isCLS;
    }

    /**
     * Whether this CLS light has a location description.
     *
     * @returns Whether this CLS light has a location description.
     */
    get hasLocationDescription() {
        return this.isCLS && !!this.locationDescription;
    }

    /**
     * Snaps the light position to the nearest ceiling grid point.
     *
     * @param props - Contains ceilingDetails with grid tile sizes and offsets.
     * @param props.ceilingDetails - The ceiling grid tile sizes and offsets for snapping.
     */
    snappingToGrid(props: { ceilingDetails: CeilingDetails }) {
        const { tileSizeX, tileSizeY, gridOffSetX, gridOffSetY } =
            props.ceilingDetails;
        const corners = this.fixtureLatLngs;
        let smallestDeltaValue = Infinity;
        let smallestDeltaVector: { lat: number; lng: number } = {
            lat: 0,
            lng: 0,
        };
        corners.forEach((latLng) => {
            const gridPnt = Map.getNearestGridPoint(
                latLng,
                tileSizeX,
                tileSizeY,
                gridOffSetX,
                gridOffSetY,
            );
            const deltaValue = Map.distanceBetweenPoints(gridPnt, latLng);
            if (deltaValue < smallestDeltaValue) {
                smallestDeltaValue = deltaValue;
                smallestDeltaVector = Map.subtractLatLng(gridPnt, latLng);
            }
        });
        this.point.lat += smallestDeltaVector.lat;
        this.point.lng += smallestDeltaVector.lng;
    }

    /**
     * Rotates the light 90 degrees by swapping its width and length dimensions.
     */
    rotate() {
        const { width, length, ...externalDimension } = this.externalDimension;
        const {
            width: apertureWidth,
            length: apertureLength,
            ...signalAperture
        } = this.signalAperture;
        this.externalDimension = {
            ...externalDimension,
            width: length,
            length: width,
        };
        this.signalAperture = {
            ...signalAperture,
            width: apertureLength,
            length: apertureWidth,
        };
    }

    /**
     * Checks whether this light overlaps any other lights in the given collection.
     *
     * @param lights - The collection of lights to check for overlaps against.
     * @returns Whether this light overlaps any other light.
     */
    isOverlapping(lights: Light[]) {
        if (this.bounds && this.bounds.pad) {
            const paddedBoundsA = this.bounds.pad(-0.01);
            return _.some(lights, (light) => {
                if (this.uniqueId === light.uniqueId) {
                    return false;
                }
                if (light.bounds && light.bounds.pad) {
                    const paddedBoundsB = light.bounds.pad(-0.01);
                    return (
                        paddedBoundsA.intersects(paddedBoundsB) ||
                        paddedBoundsA.overlaps(paddedBoundsB) ||
                        paddedBoundsA.contains(paddedBoundsB)
                    );
                }
                return false;
            });
        }
        return false;
    }

    /**
     * Converts this light instance to the API request format.
     *
     * @returns The API request object.
     */
    convertToAPI() {
        return {
            ...this.options,
            BLEBeaconId: this.bleId,
            BLEBeaconUUId: this.bleUUID,
            VLCEnabled: this.vlcIsActive,
            id: this.id,
            luminaireType_ID: this.type.id,
            luminaireTypeDisplayName: this.type.name,
            locationDescription: this.locationDescription,
            externalLength: Map.Converter.convertMeterToFeet(
                this._externalDimension.length,
                6,
            ),
            externalWidth: Map.Converter.convertMeterToFeet(
                this._externalDimension.width,
                6,
            ),
            flagNotes: this.notes,
            floorSpace_Id: this.floorSpaceId,
            luminairePositionId: this.positionId,
            tag: _.sumBy(this.flags, (flag) => flag.value),
            z: this.positionZ,
            ...this.invertedDimensions,
        };
    }

    static reduceLights = (lights: Light[]) =>
        _.reduce(
            lights,
            (result, light) => {
                result[light.uniqueId] = _.cloneDeep(light);
                return result;
            },
            {} as Record<string, Light>,
        );
}

export default Light;
